import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, CalendarClock, ShieldAlert, UserX, Flag, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { ROLES, type ProgramLeadQuickActionsData } from '@app-types';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import { useMissingContractRoleCount } from '@hooks/useMissingContractRoleCount';
import { hasRole } from '@utils/permissionUtils';
import styles from './ProgramLeadQuickActions.module.css';

interface ProgramLeadQuickActionsProps {
  data: ProgramLeadQuickActionsData | undefined;
  isLoading: boolean;
}

const ProgramLeadQuickActions: React.FC<ProgramLeadQuickActionsProps> = ({ data, isLoading }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessScheduler } = usePermissions();
  // Missing Contract Role resolves to Admin only; a Program Lead alone would see 0 and an empty list
  const isAdmin = hasRole(user, ROLES.ADMIN);
  const missingRoleQuery = useMissingContractRoleCount(isAdmin);

  const actions = [
    {
      label: 'Division Conflicts',
      count: data?.division_conflicts ?? 0,
      icon: GitBranch,
      iconVariant: styles.iconWarning,
      route: '/conflicts/division',
    },
    ...(isAdmin
      ? [
          {
            label: 'Missing Contract Role',
            count: missingRoleQuery.data?.total ?? 0,
            icon: UserX,
            iconVariant: styles.iconWarning,
            route: '/conflicts/missing-contract-role',
          },
        ]
      : []),
    {
      label: 'Pending Scope Changes',
      count: data?.pending_scope_changes ?? 0,
      icon: Flag,
      iconVariant: styles.iconWarning,
      route: '/conflicts/pending-scope',
    },
    {
      label: 'Meetings Pending Reschedule',
      count: data?.meetings_pending_reschedule ?? 0,
      icon: CalendarClock,
      iconVariant: styles.iconDanger,
      // Program Lead has no Scheduler worklist; the Assessment Meetings list shows the same meetings (ADO-1176)
      route: `${canAccessScheduler ? '/scheduler' : '/assessor/meetings'}?myMeetings=false&status=scheduled`,
    },
    {
      label: 'Escalated Assessments',
      count: data?.escalated_assessments ?? 0,
      icon: ShieldAlert,
      iconVariant: styles.iconInfo,
      route: '/assessments?status=Escalated',
    },
  ];

  if (isLoading) {
    return null;
  }

  return (
    <div className={styles.quickActionsContainer}>
      <div className={styles.actionsHeader}>
        <h3>Quick Actions</h3>
        <p>Exception items requiring your intervention</p>
      </div>
      <div className={styles.actionsGrid}>
        {actions.map((action) => {
          const IconComponent = action.icon;
          return (
            <button
              type="button"
              key={`action-${action.label}`}
              className={styles.actionTile}
              onClick={() => navigate(action.route)}
            >
              <div className={clsx(styles.actionIcon, action.iconVariant)}>
                <IconComponent size={24} />
              </div>
              <div className={styles.actionContent}>
                <p className={styles.actionLabel}>{action.label}</p>
                <p className={styles.actionCount}>{action.count}</p>
              </div>
              <ChevronRight size={20} className={styles.actionArrow} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { ProgramLeadQuickActions };
